'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { APP_ROUTES } from '@/app/routes';
import { Container } from '@/atoms/Container/Container';
import { Heading } from '@/atoms/Heading/Heading';
import { Typography } from '@/atoms/Typography/Typography';
import { useFollowUser } from '@/hooks/useFollowUser/useFollowUser';
import { useMutedUsers } from '@/hooks/useMutedUsers/useMutedUsers';
import { useUserStream } from '@/hooks/useUserStream/useUserStream';
import { cn } from '@/libs/utils/utils';
import type { Pubky } from '@/models/models.types';
import type { UserStreamId } from '@/models/stream/user/userStream.types';
import { useAuthStore } from '@/stores/auth/auth.store';
import { useHotStore } from '@/stores/hot/hot.store';
import { FullUserListItemSkeleton } from '../FullUserListItemSkeleton/FullUserListItemSkeleton';
import { UserListItem } from '../UserListItem/UserListItem';
import type { HotActiveUsersProps } from './HotActiveUsers.types';

const DEFAULT_USERS_LIMIT = 10;

/**
 * Get the user stream ID based on reach and timeframe filters.
 * Uses influencers source with dynamic reach/timeframe from hot store.
 * Pattern: influencers:timeframe:reach
 */
function useActiveUsersStreamId(): UserStreamId {
  const reach = useHotStore((state) => state.reach);
  const timeframe = useHotStore((state) => state.timeframe);

  const streamId = useMemo(() => {
    // Build influencers stream ID: influencers:timeframe:reach
    return `influencers:${timeframe}:${reach}` as UserStreamId;
  }, [reach, timeframe]);

  return streamId;
}

/**
 * HotActiveUsers
 *
 * Organism that displays active/influential users in a full list format.
 * Uses influencers stream with reach and timeframe filters from hot store.
 * - ALL: Influencers across all users
 * - FOLLOWING: Influencers among people the current user follows
 * - FRIENDS: Influencers among the current user's friends
 */
export function HotActiveUsers({
  limit = DEFAULT_USERS_LIMIT,
  className,
  hideHeadingOnMobile = false,
}: HotActiveUsersProps) {
  const router = useRouter();
  const currentUserPubky = useAuthStore((state) => state.currentUserPubky);
  const streamId = useActiveUsersStreamId();

  const { users, isLoading, error } = useUserStream({
    streamId,
    limit,
    includeCounts: true,
    includeRelationships: true,
    includeTags: true,
  });

  const { toggleFollow, isUserLoading } = useFollowUser();
  const { isMuted } = useMutedUsers();

  const visibleUsers = users.filter((user) => !isMuted(user.id));

  const handleUserClick = (pubky: Pubky) => {
    router.push(`${APP_ROUTES.PROFILE}/${pubky}`);
  };

  return (
    <Container overrideDefaults className={cn('flex w-full flex-col gap-2', className)} data-testid="hot-active-users">
      <Heading
        level={5}
        size="lg"
        className={cn('font-light text-muted-foreground', hideHeadingOnMobile && 'sr-only lg:not-sr-only')}
      >
        {'Active users'}
      </Heading>
      {error ? (
        <Typography className="text-destructive">{'Failed to load users'}</Typography>
      ) : isLoading ? (
        <Container className="gap-3.5 rounded-md py-2 lg:gap-3">
          {Array.from({ length: limit }).map((_, index) => (
            <FullUserListItemSkeleton key={`hot-active-users-skeleton-${index}`} />
          ))}
        </Container>
      ) : visibleUsers.length === 0 ? (
        <Typography className="font-light text-muted-foreground">{'No users to show'}</Typography>
      ) : (
        <Container className="gap-3.5 rounded-md py-2 lg:gap-3">
          {visibleUsers.map((user) => (
            <UserListItem
              key={user.id}
              user={user}
              variant="full"
              isLoading={isUserLoading(user.id)}
              isStatusLoading={isLoading}
              isCurrentUser={currentUserPubky === user.id}
              onUserClick={handleUserClick}
              onFollowClick={toggleFollow}
            />
          ))}
        </Container>
      )}
    </Container>
  );
}
